import * as THREE from "three";
import { fitToHeight } from "../core/modelFit.js";
import { CharacterAnimator } from "./CharacterAnimator.js";

/**
 * Pet — a companion that follows the hero and contributes via its role. Built
 * from a creature model (Quaternius) or, when `def.model` is null, a procedural
 * Arcane Wisp. Passive roles buff the hero (via petMods, applied by Game);
 * active roles (collector / hunter) act each frame in update().
 */
const MAGNET_R = 4.6, PULL = 7.5, COLLECT_R = 1.35;

export class Pet {
  constructor(scene, registry, def, level = 1) {
    this.scene = scene; this.registry = registry;
    this.def = def; this.level = level;
    this.phase = 0;   // orbit phase offset (so two companions sit on opposite sides)
    this.t = Math.random() * 6.28;
    this.cd = 1.0;
    this.bolts = [];
    this.group = new THREE.Group();
    const tint = def.tint || 0xbfa8ff;

    if (!def.model) {
      this._buildWisp(tint);
      this.floatY = 1.95;
    } else if (registry.has(def.model)) {
      const model = registry.instance(def.model);
      fitToHeight(model, 1.1);
      model.traverse((o) => { if (o.isMesh) o.castShadow = true; });
      this.model = model; this.group.add(model);
      const clips = [...(model.animations || [])];
      if (clips.length) this.animator = new CharacterAnimator(model, clips);
      this.light = new THREE.PointLight(tint, 1.0, 6, 1.8);
      this.light.position.y = 1.0; this.group.add(this.light);
      this.floatY = 0.55;   // hover just off the ground
    } else {
      this._buildWisp(tint);   // model missing → fall back to a wisp
      this.floatY = 1.95;
    }
    scene.add(this.group);
  }

  _buildWisp(tint) {
    this.core = new THREE.Mesh(new THREE.SphereGeometry(0.15, 16, 16), new THREE.MeshBasicMaterial({ color: 0xf0e0ff }));
    this.group.add(this.core);
    this.glow = new THREE.Sprite(new THREE.SpriteMaterial({ map: this._glowTex(), color: tint, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
    this.glow.scale.set(1.3, 1.3, 1); this.group.add(this.glow);
    this.motes = [];
    const mm = new THREE.MeshBasicMaterial({ color: tint });
    for (let i = 0; i < 3; i++) { const m = new THREE.Mesh(new THREE.SphereGeometry(0.045, 8, 8), mm); m.userData.a = (i / 3) * Math.PI * 2; this.group.add(m); this.motes.push(m); }
    this.light = new THREE.PointLight(tint, 2.0, 9, 1.6); this.group.add(this.light);
  }

  _glowTex() {
    const c = document.createElement("canvas"); c.width = c.height = 64;
    const g = c.getContext("2d");
    const grad = g.createRadialGradient(32, 32, 2, 32, 32, 32);
    grad.addColorStop(0, "rgba(255,255,255,0.95)");
    grad.addColorStop(0.35, "rgba(210,150,255,0.6)");
    grad.addColorStop(1, "rgba(199,125,255,0)");
    g.fillStyle = grad; g.fillRect(0, 0, 64, 64);
    return new THREE.CanvasTexture(c);
  }

  setLevel(l) { this.level = l; }
  petMods() { return this.def.grants ? this.def.grants(this.level) : {}; }
  snapTo(pos) { this.group.position.set(pos.x - 0.8, (pos.y || 0) + this.floatY, pos.z); }

  update(dt, ctx) {
    const { player, loot, enemies, combat, onCollect } = ctx;
    this.t += dt;
    // hover target: orbit the hero at a standoff distance so they never overlap
    const R = this.def.model ? 2.0 : 1.6;   // creatures need more room than the wisp
    const ang = this.t * 0.7 + (this.phase || 0);
    const tx = player.pos.x + Math.cos(ang) * R;
    const tz = player.pos.z + Math.sin(ang) * R;
    const ty = (player.groundH || 0) + this.floatY + Math.sin(this.t * 2.2) * 0.13;
    const k = Math.min(1, dt * 4.5);
    const gp = this.group.position;
    const pdx = tx - gp.x, pdz = tz - gp.z;
    gp.x += pdx * k; gp.y += (ty - gp.y) * k; gp.z += pdz * k;
    if (Math.hypot(pdx, pdz) > 0.05) this.group.rotation.y = Math.atan2(pdx, pdz);
    if (this.animator) this.animator.update(dt);

    // wisp visuals
    if (this.glow) {
      const pulse = 1 + Math.sin(this.t * 3) * 0.12;
      this.glow.scale.set(1.3 * pulse, 1.3 * pulse, 1);
      this.light.intensity = 1.9 + Math.sin(this.t * 3) * 0.5;
      for (const m of this.motes) { const a = m.userData.a + this.t * 2.4; m.position.set(Math.cos(a) * 0.28, Math.sin(a * 1.3) * 0.1, Math.sin(a) * 0.28); }
    }

    this._updateBolts(dt, combat);

    // ---- role behaviours ----
    if (this.def.role === "collector" && loot && loot.pickups) {
      for (const p of loot.pickups) {
        const dx = player.pos.x - p.x, dz = player.pos.z - p.z; const d = Math.hypot(dx, dz);
        const r = MAGNET_R + this.level * 0.4;
        if (d < r && d > 0.001) { const s = Math.min(d, PULL * dt); p.x += (dx / d) * s; p.z += (dz / d) * s; p.mesh.position.x = p.x; p.mesh.position.z = p.z; }
      }
      if (loot.collectWithin) { const got = loot.collectWithin(player, COLLECT_R); if (onCollect) for (const it of got) onCollect(it); }
    } else if (this.def.role === "hunter" && this.def.hunter && enemies && combat) {
      this.cd -= dt;
      if (this.cd <= 0) {
        const h = this.def.hunter(this.level);
        let best = null, bd = h.range;
        for (const e of enemies) { if (e.dead) continue; const d = Math.hypot(e.pos.x - gp.x, e.pos.z - gp.z); if (d < bd) { bd = d; best = e; } }
        if (best) { this.cd = h.cd; this._fireBolt(gp, best, h.dmg); }
        else this.cd = 0.3;
      }
    }
  }

  _fireBolt(from, enemy, dmg) {
    const color = 0xff7a3d;
    const m = new THREE.Mesh(new THREE.SphereGeometry(0.13, 8, 8), new THREE.MeshBasicMaterial({ color }));
    m.position.set(from.x, 1.1, from.z);
    this.scene.add(m);
    this.bolts.push({ mesh: m, enemy, dmg });
  }

  _updateBolts(dt, combat) {
    if (!this.bolts.length) return;
    const speed = 20;
    this.bolts = this.bolts.filter((b) => {
      if (!b.enemy || b.enemy.dead) { this.scene.remove(b.mesh); return false; }
      const p = b.mesh.position, dx = b.enemy.pos.x - p.x, dz = b.enemy.pos.z - p.z;
      const d = Math.hypot(dx, dz);
      if (d < 0.5) {
        if (combat && combat.petDamage) combat.petDamage(b.enemy, b.dmg, "#ff7a3d");
        this.scene.remove(b.mesh); return false;
      }
      const s = Math.min(d, speed * dt); p.x += (dx / d) * s; p.z += (dz / d) * s;
      return true;
    });
  }

  dispose() {
    for (const b of this.bolts) this.scene.remove(b.mesh);
    this.bolts = [];
    this.scene.remove(this.group);
  }
}
